import { Fragment, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";
import styled from "styled-components";
import type { GetChargesByRelatedDriverResponse } from "@qivia/schemas-ev-charging";
import {
  dateFormatterLongMonthYear,
  formatAmount,
  List,
  Spacer,
} from "@qivia/ui";
import { Page } from "@qivia/ui/src/designSystem/components/driverAppV2/Page";
import { StyledColumn } from "@qivia/ui/src/designSystem/components/driverAppV2/layout";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { isAppV2Enabled } from "../appV2Access";
import { HomeChargeRow } from "./homeChargeRow";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { selectCompanyUuid } from "../../qrCode/vehicleLoginSlice";
import {
  getDriverChargesAsync,
  selectCharges,
  selectChargesStatus,
} from "../chargesSlice";
import { getReimbursementTotal } from "./lib";

type ChargesMonth = {
  key: string;
  date: Date;
  charges: GetChargesByRelatedDriverResponse;
};

export const RefundsHistory = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const companyUuid = useAppSelector(selectCompanyUuid);
  const charges = useAppSelector(selectCharges);
  const chargesStatus = useAppSelector(selectChargesStatus);

  useEffect(() => {
    if (chargesStatus === "idle" && companyUuid) {
      void dispatch(getDriverChargesAsync({ companyUuid }));
    }
  }, [chargesStatus, companyUuid, dispatch]);

  const homeCharges = useMemo(
    () => charges.filter((charge) => charge.type === "HOME_CHARGING"),
    [charges],
  );

  const months = useMemo(
    () =>
      homeCharges.reduce<ChargesMonth[]>((acc, charge) => {
        if (!charge.startAt) return acc;
        const date = new Date(charge.startAt);
        const key = `${date.getFullYear()}-${date.getMonth()}`;
        const month = acc.find((current) => current.key === key);
        if (month) month.charges.push(charge);
        else acc.push({ key, date, charges: [charge] });
        return acc;
      }, []),
    [homeCharges],
  );

  if (!isAppV2Enabled) return <Navigate to="/menu" replace />;

  return (
    <Page onBack={() => navigate(-1)} title={t("appV2.refunds.history")}>
      <StyledColumn>
        <Spacer y={1.5} />
        {months.map((month, index) => (
          <Fragment key={month.key}>
            {index > 0 && <Spacer y={2} />}
            <List
              title={dateFormatterLongMonthYear(month.date)}
              titleRightElement={
                <StyledTotal>{`+ ${formatAmount(getReimbursementTotal(month.charges))} €`}</StyledTotal>
              }
              rows={month.charges.map((charge) => (
                <HomeChargeRow key={charge.uuid} charge={charge} />
              ))}
            />
          </Fragment>
        ))}
      </StyledColumn>
    </Page>
  );
};

const StyledTotal = styled.span`
  ${typographies["Body/L"]}
  color: ${colors["colors/accent/600"]};
`;
